/* eslint-disable react/prop-types */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  Button,
  DatePicker,
  message,
  Divider,
  Typography,
} from 'antd';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import { RFC_PATTERN } from 'utils/patterns';
import LadaNumero from 'components/LadaNumero';
import Direccion from 'components/Direccion';
import { DATE_FRONTEND_FORMAT, discartNullValues } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import NumericInput from 'components/NumericInput';
import { InputSearchPro } from 'components/InputSearch';
import Select from 'components/Select';
import makeMessager from 'utils/complexFormsMessages';
import BusquedaAvanzadaPredio from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { isMutable } from 'utils/estadosGlobales';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import GirosDeComercios, { permissionConfig as permissionConfigGiros } from 'components/ComerciosDeEmpresas';
import { capitalize, toUpper } from 'utils/normalizers';
import {
  getClasificacionesDeComercio,
  getTiposDeEmpresas,
  getTiposDeEstablecimiento,
  getScian,
} from 'api/empresas';
import {
  baseURI,
  getEmpresa,
  getEmpresas,
  patchEmpresa,
  postEmpresa,
} from 'api/empresas/empresas';
import { getPredio, getPredios } from 'api/catastro/predio';
import { getContribuyente, getContribuyenteCaja } from 'api/empresas/contribuyentes';
import useQuery from 'hooks/useQuery';
import checkToken from 'api/utils/checkToken';
import { getRegimenCapital } from 'api/catalogos';
import { getTiposDePersonas } from 'api/catalogos/tipoDePersona';
import BusquedaAvanzadaSCIAN from 'components/BusquedaAvanzadaSCIAN';
import { getModelPermission } from 'utils/permission';
import address from 'utils/address';
import useActualizadorDeCartera,
{ permissionConfigActionsActualizadorCartera } from 'hooks/useActualizadorDeCartera';

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Title } = Typography;

export const permissionConfig = {
  permissionModel: 'empresa',
  catalogsModels: [
    'tipodeempresa',
    'contribuyente',
    'clasificaciondecomercio',
    'clasedeactividadempresarial',
    'unidadderecaudacion',
    'ciudadano',
    'estadosglobales',
    'predio',
    'sectorcomercial',
    'tipodeestablecimiento',
    'unidadoperativa',
  ],
  tabsModels: [permissionConfigGiros],
  customActions: [permissionConfigActionsActualizadorCartera.find((e) => e.padron === 2)],
};

const Comercios = ({ permission }) => {
  const {
    tiposDeEmpresa,
    clasificacionesDeComercio,
    estadosGlobales,
  } = useSelector((state) => state.catalogs);
  const [form] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [formDocuments] = Form.useForm();
  const [formSearch] = Form.useForm();
  const [formLicencia] = Form.useForm();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [mutable, setMutable] = useState(true);
  const [formAddress, setFormAddress] = useState();
  const [predio, setPredio] = useState([]);
  const [visibleBusqueda, setVisibleBusqueda] = useState(false);
  const [dataExterno, setDataExterno] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [contribuyenteFound, setContribuyenteFound] = useState([]);
  const [visibleBusquedaContribuyente, setVisibleBusquedaContribuyente] = useState(false);
  const [visibleSearchOnFilter, setVisibleSearchOnFilter] = useState(false);
  const [tipoDeEstablecimiento, setTipoDeEstablecimiento] = useState([]);
  const [regimenCapital, setRegimenCapital] = useState([]);
  const [tipoDePersona, setTipoDePersona] = useState([]);
  const [empresaSelected, setEmpresaSelected] = useState([]);
  const [visibleAdvanced, setvisibleAdvanced] = useState(false);
  const [actividadFound, setActividadFound] = useState([]);
  const { confirmUpdateCartera, updateCarteraAvailable } = useActualizadorDeCartera({
    tipoDePadronId: 2,
    padron: empresaSelected,
    setLoading,
    permission,
  });

  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const params = useQuery(['id']);

  const searchRef = useRef();
  const searchPredioRef = useRef();

  const basicKeys = [
    'general',
    'info',
    'complementarios',
    'licencia',
  ];

  const formInstances = [
    form,
    formInfo,
    formLicencia,
  ];

  const complexForms = [{
    key: 'domicilio',
    formInstance: formAddress,
    permission: addressPermission,
  }];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getEmpresas({ page, ...q });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      checkToken();
      setLoading(true);
      const [
        _regimenCapital,
        _establecimientos,
        _tiposPersonas,
      ] = await Promise.all([
        getRegimenCapital(),
        getTiposDeEstablecimiento(),
        getTiposDePersonas(),
      ]);
      await getClasificacionesDeComercio();
      await getTiposDeEmpresas();
      if (mounted) {
        setTipoDeEstablecimiento(_establecimientos);
        setRegimenCapital(_regimenCapital);
        setTipoDePersona(_tiposPersonas);
        setLoading(false);
      }
    };
    if (permission.hasAllViewPermission) {
      fetchAll();
      if (params[0] !== null) {
        setTimeout(() => {
          // eslint-disable-next-line no-use-before-define
          onClickEdit(null, params[0]);
          setSelectedRowKeys([params[0]]);
        });
      }
    }
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    formInfo.resetFields();
    setSelectedRowKeys([]);
    setCurrentTabKey('general');
    setPredio([]);
    setContribuyenteFound([]);
    setActividadFound([]);
    setMutable(true);
    setEmpresaSelected([]);
  };

  const handleSearchData = async () => {
    try {
      setLoading(true);
      await formSearch.validateFields();
      const searchParams = formSearch.getFieldsValue();
      const keys = Object.keys(searchParams);
      const validProps = keys.filter((prop) => searchParams[prop]);

      if (!validProps?.length) {
        message.warn('Debe ingresar al menos un campo.');
        return;
      }

      const _data = await getEmpresas(searchParams);
      setData(_data);
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = () => {
    if (searchRef.current) {
      clearTimeout(searchRef.current);
    }
    searchRef.current = setTimeout(handleSearchData, 500);
  };

  const wipe = () => {
    formSearch.resetFields();
    setData({ results: [] });
  };

  const contribuyenteInfo = async (_contribuyente) => {
    if (!_contribuyente) return;
    const _tipo_persona = tipoDePersona.find((t) => t.id === _contribuyente.tipo_de_persona);
    const _regimen_capital = regimenCapital.find((r) => r.id === _contribuyente?.regimen_capital);
    form.setFieldsValue({
      tipo_de_persona: _tipo_persona?.descripcion,
      nombre_comercial_contribuyente: _contribuyente?.nombre_comercial,
      razon_social: _contribuyente?.razon_social,
      regimen_capital_contribuyente: _regimen_capital?.descripcion || 'No aplica',
    });
  };

  const alternSearchContribuyente = async () => {
    try {
      setLoading(true);
      await formSearch.validateFields(['q']);
      const { q_contribuyente: q } = formSearch.getFieldsValue();
      if (!q) {
        message.info('Ingrese algún dato para buscar al Contribuyente.');
        return;
      }
      const contribuyentes = await getContribuyenteCaja({ rfc: q });

      if (!contribuyentes?.results?.length) {
        message.info('No se encontró al Contribuyente.');
        return;
      }

      const [selected] = contribuyentes.results;
      formSearch.setFieldsValue({
        contribuyente: selected.id,
      });
      setContribuyenteFound([selected]);
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const fetchContribuyente = async (id, altern = false) => {
    if (!id) return;
    setLoading(true);
    const _contribuyente = await getContribuyente(id);
    if (!_contribuyente) {
      message.info('No se encontró al contribuyente');
      setContribuyenteFound([]);
      setLoading(false);
      return;
    }
    await contribuyenteInfo(_contribuyente);
    setContribuyenteFound([_contribuyente]);
    setTimeout(() => {
      if (altern) {
        formSearch.setFieldsValue({
          contribuyente: _contribuyente?.id,
        }, 100);
      } else {
        form.setFieldsValue({
          contribuyente: _contribuyente?.id,
        }, 100);
      }
    });
    setLoading(false);
  };

  const fetchPredio = async (id) => {
    if (!id) return;

    setLoading(true);
    const _predio = await getPredio(id);

    if (!_predio) {
      message.info('No se encontró el predio');
      setPredio([]);
      setLoading(false);
      return;
    }

    setPredio([_predio]);
    setTimeout(() => { formInfo.setFieldsValue({ predio: _predio?.id }); }, 100);

    setLoading(false);
  };

  const fetchClaseScian = async (selected) => {
    setLoading(true);
    const _scian = await getScian(selected || selected?.id);
    if (_scian) {
      _scian.titulo_completo = `${_scian.codigo} - ${_scian.titulo}`;
      setTimeout(() => {
        setActividadFound([_scian]);
        formInfo.setFieldsValue({ giros_scian: _scian.id });
      }, 100);
    } else {
      setActividadFound([]);
    }
    setLoading(false);
  };

  const onClickEdit = async (responseData = null, id = null) => {
    setLoading(true);
    const key = selectedRowKeys[0] || id;
    const match = responseData || await getEmpresa(key);
    const idContribuyente = typeof match?.contribuyente === 'object'
      ? match?.contribuyente?.id : match?.contribuyente;
    const idPredio = typeof match?.predio === 'object'
      ? match?.predio?.id : match?.predio;

    if (idPredio) {
      const _predio = await getPredio(idPredio);
      setPredio([_predio]);
    }
    setTimeout(() => {
      if (match) {
        setEmpresaSelected(match);
        setMutable(isMutable(match));
        // form.resetFields();
        setTimeout(async () => {
          await fetchContribuyente(idContribuyente);
          form.setFieldsValue(match);
          if (match?.giros_scian) {
            await fetchClaseScian(match.giros_scian);
          }
          formInfo.setFieldsValue(match);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const validateAllForms = async () => {
    await Promise.all([
      form.validateFields(),
      formInfo.validateFields(),
    ]);
  };

  const getAllFormValues = () => {
    const values = {
      ...form.getFieldsValue(),
      ...formInfo.getFieldsValue(),
      ...formDocuments.getFieldsValue(),
      ...formLicencia.getFieldsValue(),
    };

    delete values.documentos_de_tramite;

    //   ['contribuyente', 'ciudadano', 'predio'].forEach((key) => {
    //     const value = values?.[key];
    //     if (typeof value === 'object' && typeof value !== 'number' && 'id' in value) {
    //       values[key] = value.id;
    //     }
    //   });

    //   return values;
    // };
    ['contribuyente', 'ciudadano', 'predio']?.forEach((key) => {
      const value = values?.[key]?.id;
      if (value) {
        values[key] = value.id;
      }
    });

    return values;
  };

  const handleContinue = async (_data, key) => {
    if (!key) setSelectedRowKeys([_data.id]);
    await onClickEdit(_data, _data.id);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await validateAllForms();
      const [key] = selectedRowKeys;
      const values = getAllFormValues();
      const _data = key ? await patchEmpresa(key, values) : await postEmpresa(values);

      if (_data) {
        if (_continue) {
          await handleContinue(_data, key);
        }
        if (updateCarteraAvailable) {
          confirmUpdateCartera(_continue ? null : onCancel);
        } else if (!_continue) {
          onCancel();
        }
      }

      setLoading(false);
      return _data || true;
    } catch (err) {
      onError(err, setLoading, formInstances, setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await patchEmpresa(key, { estados_globales: 5 });
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await handleSearchData();
      }
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.resetFields();
    setTimeout(() => {
      const values = discartNullValues(record);
      form.setFieldsValue(values);
      formInfo.setFieldsValue(values);
      formDocuments.setFieldsValue(values);
      formLicencia.setFieldsValue(values);
    });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      key: 'clave',
      dataIndex: 'clave',
      width: 150,
    },
    {
      titleText: 'Denominación/Nombre comercial',
      key: 'nombre_comercial',
      dataIndex: 'nombre_comercial',
      width: 300,
    },
    {
      titleText: 'Clave contribuyente',
      key: 'contribuyente__clave',
      dataIndex: 'contribuyente__clave',
      render: (_, record) => record.contribuyente?.clave,
      width: 300,
      allowFilter: false,
    },
    {
      titleText: 'Nombre contribuyente',
      key: 'contribuyente__nombre_completo',
      dataIndex: 'contribuyente__nombre_completo',
      render: (_, record) => record.contribuyente?.nombre_completo,
      width: 300,
    },
    {
      titleText: 'RFC',
      key: 'contribuyente__rfc',
      dataIndex: 'contribuyente__rfc',
      render: (_, record) => record.contribuyente?.rfc,
      width: 300,
    },
    {
      titleText: 'Establecimiento',
      key: 'tipo_de_empresa',
      dataIndex: 'tipo_de_empresa',
      render: (text) => tiposDeEmpresa.find((item) => item.id === text)?.descripcion || 'N/A',
      width: 300,
    },
    {
      titleText: 'Ubicacion de Comercio',
      key: 'direccion_de_ubicacion',
      dataIndex: 'direccion_de_ubicacion',
      render: (text) => (text ? address(text) : 'El registro no tiene direccion'),
      width: 300,
    },
    {
      titleText: 'Estado',
      key: 'estados_globales',
      dataIndex: 'estados_globales',
      width: 150,
      render: (text) => estadosGlobales.find((e) => e.id === text)?.descripcion,
    },
  ];

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    RFC: [
      {
        validator: async (rule, value) => {
          const { es_persona_fisica } = formInfo.getFieldValue();
          if (value) {
            if (!RFC_PATTERN.test(value)
              || (!es_persona_fisica && value.length === 13)
              || (es_persona_fisica && value.length === 12)) {
              throw new Error('Ingrese un RFC válido');
            }
          }
        },
      },
    ],
    sitio_web: [
      {
        type: 'url',
        message: 'Ingrese una URL válida',
      },
    ],
    email: [
      { type: 'email' },
    ],
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const resetAndValidateContribuyente = () => {
    form.setFieldsValue({
      tipo_de_persona: '',
      razon_social: '',
      nombre_comercial_contribuyente: '',
      regimen_capital_contribuyente: '',
    });
    setContribuyenteFound([]);
    form.resetFields(['q_contribuyente']);
    form.validateFields(['q_contribuyente']);
    formSearch.resetFields(['q_contribuyente']);
    formSearch.validateFields(['q_contribuyente']);
  };

  const searchContribuyente = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q_contribuyente: q } = form.getFieldsValue();
      if (!q) {
        message.info('Ingrese algún dato para buscar al Contribuyente.');
        return;
      }
      const contribuyentes = await getContribuyenteCaja({ rfc: q });

      if (!contribuyentes?.results?.length) {
        message.info('No se encontró al Contribuyente.');
        return;
      }

      const [selected] = contribuyentes.results;
      form.setFieldsValue({
        contribuyente: selected.id,
        razon_social: selected.razon_social,
      });
      await contribuyenteInfo(contribuyentes.results[0]);
      setContribuyenteFound([selected]);
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  // const searchContribuyente

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  const handleSearch = async () => {
    setLoading(true);
    await formInfo.validateFields(['q']);
    const { q } = formInfo.getFieldsValue(['q']);
    if (q) {
      const _predios = await getPredios({ q });
      if (_predios?.results.length === 1) {
        const [selected] = _predios?.results;
        formInfo.setFieldsValue({
          predio: selected.id,
          participe_principal: selected.participe_principal,
        });
        setPredio(selected);
      } else if (_predios.results.length > 1) {
        setDataExterno(_predios);
        setVisibleBusqueda(true);
      } else {
        message.info('No se encontró Predio');
      }
    }
    setLoading(false);
  };

  const onSearch = () => {
    if (searchPredioRef.current) clearTimeout(searchPredioRef.current);
    searchPredioRef.current = setTimeout(handleSearch, 500);
  };

  const onSearchSCIAN = async () => {
    try {
      setLoading(true);
      await formInfo.validateFields(['q']);
      const { q } = formInfo.getFieldsValue(['q']);

      if (!q) {
        message.info('Ingrese algún dato para buscar la actividad SCIAN');
        setLoading(false);
        return;
      }

      const _scian = await getScian({ q });

      if (!_scian.results.length) {
        message.info('No se encontró actividad SCIAN');
        setLoading(false);
        return;
      }

      const [selected] = _scian.results;
      formInfo.setFieldsValue({ giros_scian: selected.id });
      setActividadFound([selected]);
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys,
    complexForms,
    currentTabKey,
    setCurrentTabKey,
    showMsg,
  );

  const resetAndValidateActividad = () => {
    form.resetFields();
    setActividadFound([]);
    form.validateFields();
  };

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);
  const _onFinish = getOnFinish();

  return (
    <Row align="center" justify="center" className="container">
      <Col xs={24} sm={24} md={24}>
        <Spin tip="Cargando..." spinning={loading}>
          {!visible ? (
            <>
              <Form
                layout="vertical"
                form={formSearch}
                scrollToFirstError
                onFinish={() => debouncedSearch()}
              >
                <Row gutter={10}>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Clave"
                      name="clave"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Denominación/Nombre comercial"
                      name="nombre_comercial"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Página web"
                      name="pagina_web"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Tipo de establecimiento"
                      name="tipo_de_establecimiento"
                      normalize={capitalize}
                    >
                      <Select
                        dataSource={tipoDeEstablecimiento}
                        labelProp="nombre"

                      />
                    </Form.Item>
                  </Col>
                  <InputSearchPro
                    label="RFC de Contribuyente"
                    name="q_contribuyente"
                    onClickAdvanced={() => setVisibleSearchOnFilter(true)}
                    breakPoints={{
                      xs: 24,
                      sm: 24,
                      md: 8,
                    }}
                    inputProps={{
                      onPressEnter: () => alternSearchContribuyente(),
                      onChange: ({ target: { value } }) => {
                        if (!value) {
                          resetAndValidateContribuyente();
                        }
                      },
                    }}
                    resultProps={{
                      label: 'RFC',
                      name: 'contribuyente',
                      dataSource: contribuyenteFound,
                      labelProp: 'rfc',
                      onClear: resetAndValidateContribuyente,
                      breakPoints: {
                        xs: 24,
                        sm: 24,
                        md: 8,
                      },
                    }}
                  />
                  <Col xs={24} sm={24} md={8} />
                  <Col span={24}>
                    <Divider style={{ marginTop: 0 }}>Por Dirección</Divider>
                  </Col>
                  <Col md={8} sm={24} xs={24}>
                    <NumericInput
                      label="Código postal"
                      name="domicilio_fiscal__codigo_postal"
                      normalize={capitalize}
                      maxLength={5}
                    />
                  </Col>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Calle principal"
                      name="domicilio_fiscal__calle_principal"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Número exterior"
                      name="domicilio_fiscal__numero_exterior"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24} lg={8}>
                    <Button
                      htmlType="submit"
                      block
                      style={{ marginTop: 21 }}
                      icon={<SearchOutlined />}
                    >
                      Buscar
                    </Button>
                  </Col>
                  <Col xs={24} md={24} lg={8}>
                    <Button
                      onClick={wipe}
                      block
                      style={{ marginTop: 21 }}
                      icon={<DeleteOutlined />}
                    >
                      Limpiar Busqueda
                    </Button>
                  </Col>
                </Row>
              </Form>
              <Table
                cols={columns}
                data={data?.results}
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                deleteText="Cancelar"
                controls={{
                  onClickAdd,
                  onClickEdit: () => onClickEdit(null),
                  onClickDelete,
                }}
                rowKey="id"
                permission={permission}
                pagination={false}
                paginationCallback={fetchData}
                count={data.count}
                currentPage={currentPage}
                allowExpand
              />
            </>
          ) : (
            <Card
              className="form-container no-padding"
              title={`${selectedRowKeys.length
                ? `Editando: ${empresaSelected?.clave} - ${empresaSelected?.nombre_comercial
                || ''}` : 'Agregar'} Comercio`}
              extra={(
                <FormSubmitControls
                  onFinish={!mutable ? null : _onFinish}
                  onCancel={onCancel}
                  mutable={mutable}
                  allowCancel
                  onCancelText="Regresar"
                  permission={permission}
                  allowSaveAndContinue={mutable}
                  loading={loading}

                />
              )}
              bordered={false}
            >
              <Tabs
                onChange={mutable ? onChangeTabKey : setCurrentTabKey}
                activeKey={currentTabKey}
                type="card"
              >
                <TabPane tab="Datos Generales" key="general" forceRender>
                  <Form
                    layout="vertical"
                    name="general"
                    form={form}
                    onFinish={() => onFinish()}
                    scrollToFirstError
                    initialValues={{ estados_globales: 1 }}
                  >
                    <Row gutter={10}>
                      {!!selectedRowKeys.length && (
                        <Col xs={24} sm={24} md={4}>
                          <Form.Item
                            name="clave"
                            label="Clave"
                            hasFeedback
                          >
                            <Input disabled allowClear />
                          </Form.Item>
                        </Col>
                      )}
                      <Col xs={24} sm={24} md={12}>
                        <Form.Item
                          name="nombre_comercial"
                          label="Denominación/Nombre comercial"
                          maxLength={250}
                          normalize={toUpper}
                          rules={rules.required}
                        >
                          <Input disabled={!mutable} allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={24} />
                      <InputSearchPro
                        label="RFC de Contribuyente"
                        name="q_contribuyente"
                        required
                        disabled={!mutable}
                        onClickAdvanced={() => setVisibleBusquedaContribuyente(true)}
                        breakPoints={{
                          xs: 24,
                          sm: 24,
                          md: 8,
                        }}
                        inputProps={{
                          onPressEnter: () => searchContribuyente(),
                          onChange: ({ target: { value } }) => {
                            if (!value) {
                              resetAndValidateContribuyente();
                            }
                          },
                        }}
                        resultProps={{
                          label: 'RFC',
                          name: 'contribuyente',
                          dataSource: contribuyenteFound,
                          labelProp: 'rfc',
                          onClearConfirm: resetAndValidateContribuyente,
                          contentConfirm: 'Si se realiza, se quitará cualquier relación con la empresa.',
                          breakPoints: {
                            xs: 24,
                            sm: 24,
                            md: 8,
                          },
                        }}
                      />
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="tipo_de_persona"
                          label="Tipo de Persona"
                        >
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="razon_social"
                          label="Razón Social"
                          normalize={toUpper}
                          rules={rules.required}
                        >
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="regimen_capital_contribuyente"
                          label="Régimen Societario o de Capital"
                        >
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="nombre_comercial_contribuyente"
                          label="Nombre comercial"
                        >
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={4} />
                      <Col xs={24} sm={24} md={24} />
                      <Title level={4}>Datos de contacto</Title>
                      <Divider orientation="left" orientationMargin="0" style={{ marginTop: 0 }} />
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="nombre_del_gerente_propietario"
                          label="Nombre de Gerente Propietario"
                          maxLength={250}
                          rules={rules?.required}
                        >
                          <Input disabled={!mutable} allowClear />
                        </Form.Item>
                      </Col>
                      <Col md={12} />
                      <LadaNumero
                        form={form}
                        names={{ lada: 'lada_fijo', numero_de_telefono: 'telefono_fijo' }}
                        tooltip={(
                          <span>
                            Teléfono Fijo de la Empresa
                            <br />
                            <b>Ejemplo: 6536906857</b>
                          </span>
                        )}
                        label="Teléfono Fijo"
                        required={false}
                        disabled={!mutable}
                      />
                      <LadaNumero
                        form={form}
                        disabled={!mutable}
                        names={{ lada: 'lada_celular', numero_de_telefono: 'telefono_celular' }}
                        tooltip={(
                          <span>
                            Teléfono Celular de la Empresa
                            <br />
                            <b>Ejemplo: 6536906857</b>
                          </span>
                        )}
                        label="Teléfono Celular"
                        required={false}
                      />
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="sitio_de_internet"
                          rules={rules.sitio_web}
                          label="Sitio de Internet"
                          hasFeedback
                        >
                          <Input disabled={!mutable} allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="correo_electronico"
                          rules={rules.email}
                          label="Correo electrónico"
                          hasFeedback
                        >
                          <Input disabled={!mutable} allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="estados_globales"
                          label="Estado"
                          hasFeedback
                        >
                          <Select
                            disabled
                            dataSource={estadosGlobales}
                          />
                        </Form.Item>
                      </Col>

                    </Row>
                    {/* <Form.Item hidden>
                      <Button htmlType="submit" />
                    </Form.Item> */}
                  </Form>
                </TabPane>
                <TabPane
                  tab="Domicilio"
                  key="domicilio"
                  disabled={!selectedRowKeys.length}
                  forceRender={selectedRowKeys.length}
                >
                  <Direccion
                    mutable={mutable}
                    currentURI={baseURI}
                    parentID={selectedRowKeys[0]}
                    setLoading={setLoading}
                    onError={onError}
                    setFormAddress={setFormAddress}
                    setCurrentTabKey={setCurrentTabKey}
                    addressPropName="direccion_de_ubicacion"
                    allowVerify
                    callback={(values, _continue) => {
                      if (!_continue) {
                        onCancel();
                      } else {
                        const [key] = selectedRowKeys;
                        const clone = [...data.results];
                        const mappedData = clone.map((e) => (e.id === key
                          ? ({ ...e, direccion_de_ubicacion: values?.id }) : e));
                        setData(mappedData);
                      }
                    }}
                    permission={addressPermission}
                  />
                </TabPane>
                <TabPane tab="Clasificación Empresarial" key="info" forceRender>
                  <Form
                    name="info"
                    layout="vertical"
                    form={formInfo}
                    onFinish={() => onFinish()}
                    scrollToFirstError
                  >
                    <Row gutter={10}>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="tipo_de_empresa"
                          label="Tipo de Empresa"
                          rules={rules?.required}
                        >
                          <Select
                            disabled={selectedRowKeys?.length}
                            dataSource={tiposDeEmpresa}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="tipo_de_establecimiento"
                          label="Tipo de Establecimiento"
                          hasFeedback
                        >
                          <Select
                            disabled={!mutable}
                            dataSource={tipoDeEstablecimiento}
                            labelProp="nombre"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="clasificacion_de_comercio"
                          label="Clasificación de comercio"
                          hasFeedback
                        >
                          <Select
                            disabled={!mutable}
                            dataSource={clasificacionesDeComercio}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_de_apertura"
                          label="Fecha de apertura"
                          hasFeedback
                        >
                          <DatePicker
                            disabled={!mutable}
                            format={DATE_FRONTEND_FORMAT}
                            placeholder=""
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_de_cierre"
                          label="Fecha de cierre"
                          hasFeedback
                        >
                          <DatePicker
                            disabled={!mutable}
                            format={DATE_FRONTEND_FORMAT}
                            placeholder=""
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_de_alta_de_padron_empresa"
                          label="Fecha de alta de Padrón"
                          hasFeedback
                        >
                          <DatePicker
                            disabled={!mutable}
                            format="DD/MM/YYYY"
                            placeholder=""
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="es_venta_de_alcohol"
                          label="¿Aplica Venta de Alcohol?"
                          hasFeedback
                        >
                          <Select
                            trueFalse
                            allowClear
                            disabled={!mutable}
                          />
                        </Form.Item>
                      </Col>
                      <InputSearchPro
                        label="Clase de actividad SCIAN"
                        tooltip="Búsqueda de actividades económicas"
                        name="q"
                        disabled={!mutable}
                        onClickAdvanced={() => setvisibleAdvanced(true)}
                        breakPoints={{
                          xs: 24,
                          sm: 24,
                          md: 8,
                        }}
                        inputProps={{
                          onPressEnter: () => onSearchSCIAN(),
                          onChange: ({ target: { value } }) => {
                            if (!value) {
                              resetAndValidateActividad();
                            }
                          },
                        }}
                        resultProps={{
                          dataSource: actividadFound,
                          name: 'giros_scian',
                          label: 'Clase de actividad SCIAN',
                          labelProp: 'titulo_completo',
                          onClear: resetAndValidateActividad,
                          breakPoints: {
                            xs: 24,
                            sm: 24,
                            md: 8,
                          },
                        }}
                      />
                      <Col xs={24} sm={24} md={8}>
                        <NumericInput
                          name="unidad"
                          label="Unidad"
                          allowClear
                          decimal
                          disabled={!mutable}
                        />
                      </Col>

                      <Col xs={24} sm={24} md={16}>
                        <Form.Item
                          name="horario_empresa"
                          label="Horario"
                        >
                          <Input disabled={!mutable} allowClear />
                        </Form.Item>
                      </Col>
                      {!predio.length ? (
                        <>
                          <Col xs={24} sm={24} md={24}>
                            <InputSearchPro
                              onChange={onSearch}
                              label="Clave Catastral"
                              name="q"
                              disabled={!mutable}
                              onClickAdvanced={() => setVisibleBusqueda(true)}
                              inputProps={{
                                onPressEnter: () => {
                                  clearTimeout(searchPredioRef.current);
                                  handleSearch();
                                },
                                onChange: ({ target: { value } }) => {
                                  if (!value) {
                                    resetAndValidateActividad();
                                  }
                                },
                              }}
                              resultProps={{
                                name: 'predio',
                                label: 'Clave Catastral',
                                dataSource: predio,
                                disabled: mutable,
                                labelProp: 'participe_principal',
                                onClear: () => setPredio([]),
                                breakPoints: {
                                  xs: 24,
                                  sm: 24,
                                  md: 8,
                                },
                              }}
                              title="Búsqueda por Cuenta Única Predial"
                            />
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="porcentaje_de_multa"
                              label="Porcentaje de Multa Extemporanea"
                              initialValue={70.0}
                              rules={[
                                { required: true, message: 'Este campo es obligatorio' },
                                {
                                  validator: (_, value) => {
                                    if (value < 30 || value > 70) {
                                      return Promise.reject(new Error('Debe estar entre 30.00% y 70.00%'));
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <NumericInput
                                decimal
                                precision={2}
                                min={30}
                                max={70}
                                addonAfter="%"
                                disabled={!mutable}
                              />
                            </Form.Item>
                          </Col>

                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="porcentaje_de_recargo"
                              label="Porcentaje de Recargo"
                              initialValue={2.0}
                              rules={[
                                { required: true, message: 'Este campo es obligatorio' },
                                {
                                  validator: (_, value) => {
                                    if (value < 0 || value > 2) {
                                      return Promise.reject(new Error('Debe estar entre 0.00% y 2.00%'));
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <NumericInput
                                decimal
                                precision={2}
                                min={0}
                                max={2}
                                addonAfter="%"
                                disabled={!mutable}
                              />
                            </Form.Item>
                          </Col>

                          <Col xs={24} sm={24} md={0} />
                        </>
                      ) : (
                        <>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="predio"
                              label="Clave Catastral"
                            >
                              <Select
                                disabled={!mutable}
                                dataSource={predio}
                                onChange={(val) => {
                                  if (!val) {
                                    setPredio([]);
                                    // formFiscales.resetFields(['q']);
                                  }
                                }}
                                render={(e) => `${e.id} - ${e?.participe_principal}`}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={16} />
                        </>
                      )}
                      <Col xs={24} sm={24} md={8}>
                        <NumericInput
                          name="superficie_del_terreno"
                          label="Sup. del Terreno (m2)"
                          disabled={!mutable}
                          allowClear
                          decimal
                          commas
                        />
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <NumericInput
                          name="superficie_construida_del_terreno"
                          label="Sup. Construida del Terreno (m2)"
                          disabled={!mutable}
                          allowClear
                          decimal
                          commas
                        />
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <NumericInput
                          name="superficie_empresa"
                          label="Sup. de la Empresa (m2)"
                          disabled={!mutable}
                          allowClear
                          decimal
                          commas
                        />
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          name="observaciones"
                          label="Observaciones"
                        >
                          <TextArea
                            allowClear
                            autoSize={{ minRows: 4, maxRows: 5 }}
                            maxLength={250}
                            disabled={!mutable}
                          />
                        </Form.Item>
                      </Col>
                      {!!selectedRowKeys?.length && (
                        <>
                          <Col xs={24} sm={24} md={8} />
                          <Title level={4}>Giros de Comercio</Title>
                          <GirosDeComercios
                            justView={!mutable}
                            empresa={selectedRowKeys[0]}
                            permission={permission.tabs.girocomercial}
                          />
                        </>
                      )}
                    </Row>
                    <Form.Item hidden>
                      <Button htmlType="submit" />
                    </Form.Item>
                  </Form>
                </TabPane>
                <TabPane tab="Datos de Licencia" key="licencia">
                  <Form
                    name="licencia"
                    layout="vertical"
                    form={formLicencia}
                    onFinish={() => onFinish()}
                    scrollToFirstError
                  >
                    <Row gutter={10}>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="numero_de_licencia"
                          label="Número de Licencia"
                        >
                          <Input disabled={!mutable} allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_expedicion_licencia"
                          label="Fecha de Expedición de Licencia"
                        >
                          <DatePicker
                            disabled={!mutable}
                            format={DATE_FRONTEND_FORMAT}
                            placeholder=""
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_vencimiento_licencia"
                          label="Fecha de Vencimiento de Licencia"
                        >
                          <DatePicker
                            disabled={!mutable}
                            format={DATE_FRONTEND_FORMAT}
                            placeholder=""
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="dictamen"
                          label="Número de Expediente"
                        >
                          <Input disabled={!mutable} allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="dictamen_registro"
                          label="Dictamen de Registro"
                        >
                          <Input disabled={!mutable} allowClear />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item hidden>
                      <Button htmlType="submit" />
                    </Form.Item>
                  </Form>
                </TabPane>
              </Tabs>
            </Card>
          )}
          <BusquedaAvanzadaPredio
            tipoDePadron={15}
            visible={visibleBusquedaContribuyente}
            setVisible={setVisibleBusquedaContribuyente}
            callback={(_contribuyente) => fetchContribuyente(_contribuyente?.id || _contribuyente)}
          />
          <BusquedaAvanzadaPredio
            tipoDePadron={15}
            visible={visibleSearchOnFilter}
            setVisible={setVisibleSearchOnFilter}
            // eslint-disable-next-line max-len
            callback={(_contribuyente) => fetchContribuyente(_contribuyente?.id || _contribuyente, true)}
          />
          <BusquedaAvanzadaPredio
            tipoDePadron={3}
            visible={visibleBusqueda}
            setVisible={setVisibleBusqueda}
            callback={(selected) => {
              fetchPredio(selected.id);
              setPredio([selected]);
            }}
          />
          <BusquedaAvanzadaSCIAN
            visible={visibleAdvanced}
            setVisible={setvisibleAdvanced}
            callback={(_scian) => fetchClaseScian(_scian.id)}
            dataExterno={dataExterno}
            setDataExterno={setDataExterno}
          />
          <ModalDelete
            onDelete={deleteItem}
            onCancel={() => setVisibleAlert(false)}
            visible={visibleAlert}
            content={`la Empresa ${form.getFieldValue('nombre_comercial')}`}
          />
        </Spin>
      </Col>
    </Row>
  );
};

export default Comercios;
